import { useEffect, useState } from "react";

import styles from "./TouchThroughWrapper.module.css";

import elementsJsonData from "../../../../../assets/traitInfo/elements.json";
import patternsJsonData from "../../../../../assets/traitInfo/patterns.json";

import {
  dimensionData,
  dimensionIcons,
} from "@/assets/traitInfo/dimensionData";
import { useModalContext } from "@/context/ModalContext";
import { ElementNoBorderOrName } from "@/components/traits/element/Element/Element";
import { PatternWithoutBorder } from "@/components/traits/pattern/Pattern/Pattern";

type Props = {
  dimensionSlug: string;
  numberOfSteps: number;
  currentStep: number;
  onNext: () => void;
  onPrevious: () => void;
  children: React.ReactNode;
  inModal?: boolean;
};

type PropsTraits = {
  type: "element" | "pattern";
  slug: string;
  numberOfSteps: number;
  currentStep: number;
  onNext: () => void;
  onPrevious: () => void;
  children: React.ReactNode;
};

export default function TouchThroughWrapper(props: Props) {
  const [paused, setPaused] = useState<boolean>(false);

  const dimension = dimensionData.find(
    (d) =>
      d.slug === props.dimensionSlug || d.longFormSlug === props.dimensionSlug
  );

  const mappedNumberOfSteps = Array.from(
    { length: props.numberOfSteps },
    (_, i) => i
  ).map((step) => {
    if (props.currentStep === step) {
      return (
        <CurrentStepWithCounter
          key={step}
          paused={paused}
          onNext={() => props.onNext()}
          inModal={props.inModal}
        />
      );
    }
    return (
      <div
        className={
          props.currentStep < step
            ? styles.stepBarInactive
            : styles.stepBarActive
        }
        key={step}
      ></div>
    );
  });

  return (
    <main
      onClick={(e) => {
        const clickX = e.clientX;
        const width = e.currentTarget.offsetWidth;
        const isLeftSide = clickX < width / 2;

        if (isLeftSide) {
          props.onPrevious();
        } else {
          props.onNext();
        }
      }}
      onTouchStart={() => {
        setPaused(true);
      }}
      onTouchEnd={() => setPaused(false)}
      onTouchCancel={() => setPaused(false)}
      onContextMenu={(e) => e.preventDefault()}
      className={styles.main}
    >
      <section className={styles.header}>
        <div className={styles.iconDiv}>
          {dimensionIcons[props.dimensionSlug].icon}
        </div>
        <h4 className={styles.dimensionTitle}>{dimension?.title}</h4>
        <div className={styles.stepContainer}>{mappedNumberOfSteps}</div>
      </section>
      {props.children}
    </main>
  );
}

export function TouchThroughWrapperTraits(props: PropsTraits) {
  const [paused, setPaused] = useState<boolean>(false);

  const element = elementsJsonData.find((e) => e.slug === props.slug);
  const pattern = patternsJsonData.find((p) => p.slug === props.slug);

  const mappedNumberOfSteps = Array.from(
    { length: props.numberOfSteps },
    (_, i) => i
  ).map((step) => {
    if (props.currentStep === step) {
      return (
        <CurrentStepWithCounter
          key={step}
          paused={paused}
          onNext={() => props.onNext()}
          inModal={true}
        />
      );
    }
    return (
      <div
        className={
          props.currentStep < step
            ? styles.stepBarInactive
            : styles.stepBarActive
        }
        key={step}
      ></div>
    );
  });

  return (
    <main
      onClick={(e) => {
        const clickX = e.clientX;
        const width = e.currentTarget.offsetWidth;
        const isLeftSide = clickX < width / 2;

        if (isLeftSide) {
          props.onPrevious();
        } else {
          props.onNext();
        }
      }}
      onTouchStart={() => {
        setPaused(true);
      }}
      onTouchEnd={() => setPaused(false)}
      onTouchCancel={() => setPaused(false)}
      onContextMenu={(e) => e.preventDefault()}
      className={styles.main}
    >
      <section style={{ paddingTop: "40px" }} className={styles.header}>
        <div
          style={{ width: "40px", height: "40px" }}
          className={styles.iconDiv}
        >
          {props.type === "element" ? (
            <ElementNoBorderOrName slug={props.slug} />
          ) : (
            <PatternWithoutBorder patternSlug={props.slug} />
          )}
        </div>
        <h4 className={styles.dimensionTitle}>
          {props.type === "element"
            ? element?.alias ?? element?.name
            : pattern?.name}
        </h4>
        <div className={styles.stepContainer}>{mappedNumberOfSteps}</div>
      </section>
      {props.children}
    </main>
  );
}

function CurrentStepWithCounter(props: {
  onNext: () => void;
  paused: boolean;
  inModal?: boolean;
}) {
  const { modalComponent } = useModalContext();

  const [progress, setProgress] = useState<number>(0);
  const [paused, setPaused] = useState<boolean>(false);

  useEffect(() => {
    const duration = 7000;
    const steps = 100;
    const intervalTime = duration / steps;

    if (paused || progress >= 100) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 1;
      });
    }, intervalTime);

    return () => clearInterval(interval);
  }, [paused, progress]);

  useEffect(() => {
    if (progress === 100) {
      props.onNext();
    }
  }, [progress]);

  useEffect(() => {
    setPaused(props.paused);
  }, [props.paused]);

  useEffect(() => {
    if (modalComponent.length > 0 && !props.inModal) {
      setPaused(true);
    } else if (modalComponent.length > 1 && props.inModal) {
      setPaused(true);
    } else {
      setPaused(false);
    }
  }, [modalComponent.length, props.inModal]);

  return (
    <div className={styles.counterStepParent}>
      <div
        style={{ width: `${progress}%` }}
        className={styles.counterStepInner}
      ></div>
    </div>
  );
}
